import { useState, useEffect, useRef, useCallback } from 'react';
import { clipAtOffset, nextClipStartOffset, prevClipStartOffset, firstPlayableOffset } from '../util/mcpbClips';

/**
 * useTimelinePlayhead — the shared MCPlayback master clock (Phase 1e, single-cam; generalizes to the
 * Phase 2a multi-cam sync engine).
 *
 * Mirrors the Java MCTimelineController model (dview MCTimelineController.startTimer): a 1Hz tick that
 * advances the playhead ONE wall-clock second per fire, with playback SPEED applied to the timer
 * INTERVAL (1000/speed ms) — NOT to the per-tick increment. The driven <video> additionally gets
 * playbackRate=speed so the picture keeps pace between ticks (wired by the consumer, not here).
 *
 * The playhead is an OFFSET in seconds from the search window start (0 .. windowLenSec). The consumer
 * maps it to the active clip via util/mcpbClips and seeks the <video>. Drift correction (|Δ|>1s) +
 * the 5s buffer-gate are the 2a concern (feature-inventory §12), intentionally NOT here.
 *
 * @param {object} opts
 * @param {number} opts.windowLenSec  span of the committed search window (seconds)
 * @param {Array}  opts.clips         the slot's clips (frozen events) — for auto-skip + prev/next-clip targets
 * @param {string} opts.windowStartTs committed window start 'YYYYMMDDHHMMSS' (clip-offset basis)
 * @param {boolean} opts.autoSkip     skip dead-air gaps to the next clip on tick (default on)
 * @param {boolean} opts.emptyWindow  the loaded window has no clip on ANY track: hold the clock (SO 38150)
 */
export const SPEEDS = [0.25, 0.5, 1, 2, 4];
export const DEFAULT_SPEED = 1;

/**
 * Pure per-tick advance: one wall-clock second forward, then (if auto-skip is on and the new position
 * lands in a gap) jump to the next clip start, or to the window end when no clip remains. Clamps at the
 * window end and reports `atEnd` so the hook can stop. Exported for direct unit testing (fake timers
 * verify the interval wiring; this verifies the math).
 *
 * An empty window (`emptyWindow`: loaded, no clip on any track) holds, auto-skip on or off. Running
 * the marker over dark tiles read as a broken player (SO 38150). dview's auto-skip rolls forward to the
 * next window with video (ADR-103); lite has no roll-forward at atEnd, so there is nothing to skip to.
 */
export const advancePlayhead = ({ offsetSec }, { windowLenSec = 0, clips = [], windowStartTs = '', autoSkip = true, emptyWindow = false } = {}) => {
  if (emptyWindow) return { offsetSec, atEnd: false };
  let next = offsetSec + 1;
  if (autoSkip && clips && clips.length && !clipAtOffset(clips, next, windowStartTs)) {
    const skip = nextClipStartOffset(clips, next, windowStartTs);
    next = skip !== null ? skip : windowLenSec; // no clip ahead -> run to the end
  }
  if (next >= windowLenSec) return { offsetSec: windowLenSec, atEnd: true };
  return { offsetSec: next, atEnd: false };
};

export const DEFAULT_MAX_STALL_SEC = 5;

export const useTimelinePlayhead = ({
  windowLenSec = 0, clips = [], windowStartTs = '', autoSkip = true, emptyWindow = false, initialOffset = 0,
  shouldHold, holdProgressing, maxStallSec = DEFAULT_MAX_STALL_SEC,
} = {}) => {
  // Seed from a saved offset (1g same-session restore) once on mount; 0 for a fresh search.
  const [offsetSec, setOffsetSec] = useState(() => {
    const n = Number(initialOffset);
    return Number.isFinite(n) && n > 0 ? n : 0;
  });
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeedState] = useState(DEFAULT_SPEED);
  // `holding` = the buffer-gate is currently withholding the clock (a genuine cell underrunning, or a
  // fresh jump still loading). Exposed so each SlotCell can gate ITS player on `playing && !holding` —
  // the clock alone held the timeline, but every per-slot player free-ran off `playing` the instant its
  // decoder was ready, so on a seek the fast cam raced ahead of a slow sibling. Gating the players on
  // this makes them all hold on their first painted frame, then start together when it releases (the
  // Java MCBufferGate model, where players ride the shared clock rather than free-running).
  const [holding, setHolding] = useState(false);
  const playingRef = useRef(playing);
  playingRef.current = playing;

  // Latest deps in a ref so the tick (subscribed only on play/speed change) reads fresh clips/window
  // without re-phasing the timer every time clips update.
  const depsRef = useRef();
  depsRef.current = { windowLenSec, clips, windowStartTs, autoSkip, emptyWindow };

  // Buffer-gate (2a-3 + progress-aware hold, ADR-030). shouldHold() = "a genuinely-playing cell is
  // mid-underrun right now" (the View computes it via mcpbSync). holdProgressing() = "a held cell is
  // STILL LOADING (its buffer is growing)". The ceiling (stuckSecRef) now bounds only a TRULY WEDGED
  // stream — buffering with NO data arriving. A slow-but-loading stream holds until it's ready (resume
  // when able), instead of the old blind 5s release that marched the timeline ahead mid-load and
  // desynced the picture (the exact churn on a full-res-only NVR). See docs/decisions/017-*.
  const holdRef = useRef();
  holdRef.current = shouldHold;
  const progressRef = useRef();
  progressRef.current = holdProgressing;
  const maxStallRef = useRef(maxStallSec);
  maxStallRef.current = maxStallSec;
  const stuckSecRef = useRef(0); // seconds the held stream has been buffering WITHOUT progress (wedged)
  // Set by a playing jump, cleared by the next tick: until then only the tick may release the gate. The
  // cells report their reloads during and after the jump's commit (a <video> only on its async loadstart),
  // so an early recheck could see the old "ready" reports and open the gate under them (86bc8571y).
  const jumpHoldRef = useRef(false);

  // Guard against a tick landing after unmount (the interval is also cleared in cleanup; this is the
  // belt-and-suspenders the "setState on unmounted component" warning asks for).
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // The 1Hz (speed-scaled) tick. The updater stays PURE (no nested setState); reaching the window end
  // is handled by the derived auto-stop effect below — the idiomatic split that keeps React happy.
  useEffect(() => {
    if (!playing) return undefined;
    const intervalMs = 1000 / speed;
    // Leading edge (no advance, no stall accrual): reflect the current buffer state the instant play
    // starts, so a resume plays at once when the cells are already painted, while a fresh autoplay (cells
    // still loading) holds immediately — instead of waiting a full tick before `holding` catches up. Uses
    // the bare "is any cell underrunning" predicate (nothing has accrued toward the ceiling yet).
    setHolding(!!(holdRef.current && holdRef.current()));
    jumpHoldRef.current = false; // a jump made before a pause does not outlive it
    const id = setInterval(() => {
      if (!mountedRef.current) return;
      jumpHoldRef.current = false;
      // Buffer-gate: while a genuine cell underruns, HOLD the clock so the grid stays together AND the
      // timeline doesn't run ahead of a still-loading picture. Progress-aware (ADR-030): while the held
      // stream is still LOADING (buffer growing), hold indefinitely — resume the instant it's ready. Only
      // a TRULY WEDGED stream (buffering with no data arriving) accrues toward the ceiling, so a dead
      // stream still can't freeze the grid forever. stuckSec resets on progress OR recovery. `hold` is
      // published to `holding` so each cell's player is gated in lockstep with the clock.
      let hold = false;
      if (holdRef.current && holdRef.current()) {
        if (progressRef.current && progressRef.current()) { stuckSecRef.current = 0; hold = true; } // loading -> hold
        else if (stuckSecRef.current < maxStallRef.current) { stuckSecRef.current += 1 / speed; hold = true; } // wedged, bounded hold
        else hold = false; // ceiling reached on a wedged stream: release so the grid recovers on its next clip
      } else {
        stuckSecRef.current = 0;
      }
      setHolding(hold);
      if (hold) return;
      setOffsetSec((prev) => advancePlayhead({ offsetSec: prev }, depsRef.current).offsetSec);
    }, intervalMs);
    return () => clearInterval(id);
  }, [playing, speed]);

  // `holding` only means anything while playing — a paused/stopped grid holds nothing (its players are
  // paused regardless). Clear it whenever playback stops so a later resume starts from a clean gate.
  useEffect(() => { if (!playing) setHolding(false); }, [playing]);

  // Stop playback once the playhead reaches the window end (derived from offset, not set inside the tick).
  useEffect(() => {
    if (playing && windowLenSec > 0 && offsetSec >= windowLenSec) setPlaying(false);
  }, [playing, offsetSec, windowLenSec]);

  // An empty window has nothing to play: stop rather than leave a "playing" transport over a held clock
  // (dview pauses here too). Play is still accepted once the window has clips again.
  useEffect(() => {
    if (playing && emptyWindow) setPlaying(false);
  }, [playing, emptyWindow]);

  const play = useCallback(() => setPlaying(true), []);
  const pause = useCallback(() => setPlaying(false), []);
  const togglePlay = useCallback(() => setPlaying((p) => !p), []);
  // Back: jump to the previous clip start. Mid-clip -> that clip's own start (restart it); pressed again
  // at the start -> the prior clip. If none before, land on the first in-window playable frame.
  // A position JUMP (prev/next/seek) reloads every cell's clip, so optimistically ENGAGE the gate the
  // instant it happens — otherwise a fast/cached cam paints + plays in the sub-tick window before the
  // regular tick observes the fresh underruns (the exact seek-desync). Only while playing; a paused jump
  // just paints the still frame (players are paused anyway) and the leading edge re-evaluates on play.
  // The tick clears `holding` again once every cell reports ready (shouldHold() false), in the same tick
  // it resumes the clock — so players and clock release together.
  const prevClip = useCallback(() => {
    setOffsetSec((prev) => {
      const { clips, windowStartTs } = depsRef.current;
      const p = prevClipStartOffset(clips, prev, windowStartTs);
      return p !== null ? p : firstPlayableOffset(clips, windowStartTs);
    });
    if (playingRef.current) { jumpHoldRef.current = true; setHolding(true); }
  }, []);
  // Forward: jump to the next clip start; stay put on the last clip.
  const nextClip = useCallback(() => {
    setOffsetSec((prev) => {
      const { clips, windowStartTs } = depsRef.current;
      const n = nextClipStartOffset(clips, prev, windowStartTs);
      return n !== null ? n : prev;
    });
    if (playingRef.current) { jumpHoldRef.current = true; setHolding(true); }
  }, []);
  const seek = useCallback((sec) => {
    const len = depsRef.current.windowLenSec || 0;
    const clamped = Math.max(0, Math.min(Math.round(sec), len));
    setOffsetSec(Number.isFinite(clamped) ? clamped : 0);
    if (playingRef.current) { jumpHoldRef.current = true; setHolding(true); }
  }, []);
  const setSpeed = useCallback((s) => { if (SPEEDS.includes(s)) setSpeedState(s); }, []);
  // Event-driven release (86bc8571y). The tick evaluated the gate once a second, so after the slowest
  // cell had painted the grid still waited up to a full second to start. The View calls this when a
  // held cell reports it is ready: open the gate now if nothing else holds it. It only releases; it
  // never engages the gate or advances the clock, which stay the tick's job.
  const recheckHold = useCallback(() => {
    if (!playingRef.current || jumpHoldRef.current) return;
    if (holdRef.current && holdRef.current()) return;
    stuckSecRef.current = 0;
    setHolding(false);
  }, []);

  return { offsetSec, playing, speed, holding, play, pause, togglePlay, prevClip, nextClip, seek, setSpeed, setOffset: setOffsetSec, recheckHold };
};

export default useTimelinePlayhead;
